<template>
  <div class="sponsor-sidebar-wrapper" :class="{ horizontal: props.horizontal }">
    <div v-if="bannersStore.activeBanners.length === 0" class="sponsor-banner-placeholder">
      <span class="text-muted text-sm">{{ props.horizontal ? 'Global Sponsors' : 'Sponsor Area' }}</span>
    </div>
    
    <TransitionGroup v-else name="banner-list" tag="div" class="sponsor-list">
      <SponsorBanner
        v-for="banner in displayBanners"
        :key="banner.id"
        :banner="banner"
        class="banner-list-item"
      />
    </TransitionGroup>
  </div>
</template>

<script setup lang="ts">
import { ref, watch, onUnmounted, onMounted, computed } from 'vue';
import { useBannersAdminStore } from '@/stores/admin/banners.admin.store';
import SponsorBanner from '@/components/ui/SponsorBanner.vue';

const props = defineProps<{
  horizontal?: boolean;
}>();

const bannersStore = useBannersAdminStore();
const displayBanners = ref<any[]>([]);
let rotationInterval: ReturnType<typeof setInterval> | null = null;
let currentIndex = 0; // The index of the NEXT banner to pull from activeBanners

const rotationLimit = computed(() => bannersStore.bannerRotationLimit || 5);

onMounted(async () => {
  await bannersStore.loadBanners();
  await bannersStore.loadBannerSettings();
});

watch(
  [() => bannersStore.activeBanners, rotationLimit],
  ([newBanners, limit]) => {
    // Keep only currently active banners in displayBanners
    const activeIds = new Set(newBanners.map(b => b.id));
    displayBanners.value = displayBanners.value.filter(b => activeIds.has(b.id));

    if (newBanners.length <= limit) {
      displayBanners.value = [...newBanners];
      stopRotation();
    } else {
      // Trim excess if limit decreased
      if (displayBanners.value.length > limit) {
        displayBanners.value = displayBanners.value.slice(0, limit);
      }
      // If we have fewer than limit displayed banners, fill it up
      if (displayBanners.value.length < limit) {
        const displayedIds = new Set(displayBanners.value.map(b => b.id));
        const remaining = newBanners.filter(b => !displayedIds.has(b.id));
        const needed = limit - displayBanners.value.length;
        displayBanners.value.push(...remaining.slice(0, needed));
      }
      
      if (currentIndex >= newBanners.length) {
        currentIndex = 0;
      }
      startRotation();
    }
  },
  { immediate: true, deep: true }
);

function startRotation() {
  stopRotation();
  const delaySeconds = bannersStore.bannerRotationInterval || 6;
  const delayMs = Math.max(delaySeconds * 1000, 2000); // Minimum 2 seconds to prevent seizure
  
  rotationInterval = setInterval(() => {
    const totalBanners = bannersStore.activeBanners;
    const limit = rotationLimit.value;
    if (totalBanners.length <= limit) {
      stopRotation();
      return;
    }
    
    // Ensure currentIndex is within bounds
    if (currentIndex >= totalBanners.length) {
      currentIndex = 0;
    }
    
    // Find the next banner that is not currently displayed
    let found = false;
    let nextBanner = totalBanners[currentIndex];
    const displayedIds = new Set(displayBanners.value.map(b => b.id));
    
    for (let i = 0; i < totalBanners.length; i++) {
      const idx = (currentIndex + i) % totalBanners.length;
      const candidate = totalBanners[idx];
      if (!displayedIds.has(candidate.id)) {
        nextBanner = candidate;
        currentIndex = (idx + 1) % totalBanners.length;
        found = true;
        break;
      }
    }
    
    if (found) {
      displayBanners.value.unshift(nextBanner);
      if (displayBanners.value.length > limit) {
        displayBanners.value.pop();
      }
    }
  }, delayMs);
}

function stopRotation() {
  if (rotationInterval) {
    clearInterval(rotationInterval);
    rotationInterval = null;
  }
}

onUnmounted(() => {
  stopRotation();
});
</script>

<style scoped>
.sponsor-sidebar-wrapper {
  display: flex;
  flex-direction: column;
  width: 100%;
}

.sponsor-banner-placeholder {
  height: 200px;
  background: var(--color-bg-secondary);
  border: 1px dashed var(--color-border);
  border-radius: var(--radius-lg);
  display: flex;
  align-items: center;
  justify-content: center;
}

.sponsor-list {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-sm);
  position: relative; /* important for transition-group FLIP */
}

.sponsor-sidebar-wrapper.horizontal .sponsor-list {
  flex-direction: row;
  justify-content: center;
  flex-wrap: wrap;
  gap: var(--spacing-md);
}

.sponsor-sidebar-wrapper.horizontal .banner-list-item {
  flex: 1;
  min-width: 280px;
  max-width: 400px;
}

/* 1. Declare transition elements */
.banner-list-item {
  transition: all 0.5s cubic-bezier(0.4, 0, 0.2, 1);
}

/* 2. Declare enter from and leave to state */
.banner-list-enter-from {
  opacity: 0;
  transform: translateY(-30px);
}

.sponsor-sidebar-wrapper.horizontal .banner-list-enter-from {
  transform: translateX(-30px);
}

.banner-list-leave-to {
  opacity: 0;
  transform: translateY(30px) scale(0.9);
}

.sponsor-sidebar-wrapper.horizontal .banner-list-leave-to {
  transform: translateX(30px) scale(0.9);
}

/* 3. Ensure leaving items are taken out of layout flow so that moving
      animations can be calculated correctly. */
.banner-list-leave-active {
  position: absolute;
  width: 100%; /* Important since position:absolute strips parent container width */
}
</style>
